import { type TFunction } from 'i18next';
import { array, number, object, type ObjectSchema, string } from 'yup';

import { type RoleTypeDto } from '@/api/generated/models/role-type-dto';
import { type UserStatusDto } from '@/api/generated/models/user-status-dto';
import { type MyProfileModalFormModel } from '@/components/company/companyUserModal/model/myProfileModalForm.model.ts';
import {
  selectSearchOptionOptionalValidation,
  selectSearchOptionRequiredValidation,
} from '@/utils/form/selectSearchOption.validation.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix';

const locationValidation = (isAdmin: boolean, t: TFunction) => {
  if (isAdmin) {
    return selectSearchOptionOptionalValidation<number>(number());
  }
  return selectSearchOptionRequiredValidation<number>(number(), t).required(t('general.required'));
};

const operatingLocationsValidation = (isAdmin: boolean, t: TFunction) => {
  if (isAdmin) {
    return array().of(selectSearchOptionOptionalValidation<number>(number()));
  }
  return array()
    .of(selectSearchOptionRequiredValidation<number>(number(), t))
    .required(t('general.required'))
    .min(1, t('general.required'));
};

export const useCompanyUserModalFormValidation = (
  isAdmin: boolean,
): ObjectSchema<MyProfileModalFormModel> => {
  const { t } = useTranslationWithPrefix('validation');

  return object().shape({
    firstName: string()
      .required(t('general.required'))
      .min(2, t('minLength', { min: 2 })),
    lastName: string()
      .required(t('general.required'))
      .min(2, t('minLength', { min: 2 })),
    location: locationValidation(isAdmin, t),
    operatingLocations: operatingLocationsValidation(isAdmin, t),
    phoneNumber: isAdmin ? string() : string().required(t('general.required')),
    role: selectSearchOptionRequiredValidation<RoleTypeDto>(string(), t).required(
      t('general.required'),
    ),
    status: selectSearchOptionRequiredValidation<UserStatusDto>(string(), t).required(
      t('general.required'),
    ),
  });
};
